'use client';

/**
 * renderers/custom-html-block.tsx — Custom HTML block with iframe isolation.
 *
 * Anti-P-382 root cause fix (2026-10-28 v2): pre-fix (PR #1480) this block
 * extracted `<style>` from the HTML and rendered it in a `<style>` tag
 * inside the block's container. This caused the CSS to affect the ENTIRE
 * page (editor + landing) — not just the custom-html block. Founder report:
 * "rompio el diseño de la pagina por que pegue una pagina html en el
 * bloque html".
 *
 * Fix de raíz: render the custom-html content inside a **sandboxed iframe**.
 * This provides COMPLETE CSS isolation:
 *   - The iframe is a separate document — its CSS cannot affect the parent.
 *   - The iframe's `<style>` only applies to the iframe's content.
 *   - The editor's CSS cannot affect the iframe's content either.
 *   - The iframe auto-resizes to fit its content (via
 *     `contentDocument.scrollHeight` on load + window resize listener).
 *
 * Security:
 *   - `sandbox` attribute set to `allow-same-origin` (needed for the iframe
 *     to load its content — NOT `allow-scripts` because the sanitizer
 *     already strips `<script>` tags).
 *   - The HTML is already sanitized by the backend (sanitizeLandingHtml)
 *     before being stored in the DB — `<script>`, `on*=` handlers,
 *     `javascript:` URLs, `expression()` are all stripped.
 *   - The iframe uses `srcdoc` (not `src`) — the content is inline, no
 *     network request.
 *
 * Spec ref: SPEC-059 (docs/SPEC-059-LANDING-PAGE-BUILDER.md).
 * Anti-P-041: 0 `as any`. Anti-P-042: 0 `console.*`.
 * Anti-P-383: file ≤500L target.
 */

import * as React from 'react';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import type { CustomHtmlBlockData } from '@/lib/landing-types';

/**
 * Build the complete HTML document for the iframe's `srcdoc` attribute.
 *
 * The iframe needs a full HTML document (with DOCTYPE, html, head, body)
 * because the user's custom HTML may include `<style>` blocks that need
 * to be in the `<head>` for proper CSS cascade.
 *
 * If the user's HTML is already a complete page (has DOCTYPE/html/body),
 * we use it as-is. If it's a fragment, we wrap it in a basic HTML document
 * structure.
 */
function buildIframeSrcDoc(html: string): string {
  // Check if the HTML is already a complete page.
  const hasDoctype = /<!DOCTYPE/i.test(html);
  const hasHtmlTag = /<html\b/i.test(html);
  const hasBodyTag = /<body\b/i.test(html);

  if (hasDoctype || hasHtmlTag || hasBodyTag) {
    // It's a complete page — use as-is.
    return html;
  }

  // It's a fragment — wrap in a basic HTML document.
  return `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body>
${html}
</body>
</html>`;
}

export function CustomHtmlBlock({ data }: { data: CustomHtmlBlockData }) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState(400); // default height

  // Anti-P-382 R1 fix: ALL hooks must be called unconditionally (Rules of
  // Hooks). The early return for empty HTML must come AFTER all hooks.
  const handleIframeLoad = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      // Access the iframe's document (same-origin via srcdoc).
      const doc = iframe.contentDocument;
      if (doc) {
        const height = doc.documentElement.scrollHeight;
        if (height > 0) {
          setIframeHeight(height);
        }
      }
    } catch {
      // Cross-origin — can't access. Keep default height.
      // This shouldn't happen with srcdoc (always same-origin).
    }
  }, []);

  // Re-measure on window resize (responsive content may change height).
  useEffect(() => {
    const handleResize = () => {
      const iframe = iframeRef.current;
      if (!iframe) return;
      try {
        const doc = iframe.contentDocument;
        if (doc) {
          const height = doc.documentElement.scrollHeight;
          if (height > 0) {
            setIframeHeight(height);
          }
        }
      } catch {
        // ignore
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Early return AFTER all hooks (Rules of Hooks compliance).
  // Anti-P-382 R1 fix: useMemo must be called unconditionally — even when
  // html is empty (the memo's body won't run, but the hook call is stable).
  // 14a (Pendiente #14): wrap srcDoc in useMemo so the iframe doesn't
  // re-render when the parent re-renders for unrelated state (perf).
  const srcDoc = useMemo(() => buildIframeSrcDoc(data.html ?? ''), [data.html]);

  if (typeof data.html !== 'string' || data.html.length === 0) {
    return null;
  }

  return (
    <section className="py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <iframe
          ref={iframeRef}
          srcDoc={srcDoc}
          onLoad={handleIframeLoad}
          title="Contenido personalizado"
          className="w-full border-0"
          style={{ height: `${iframeHeight}px`, minHeight: '200px' }}
          sandbox="allow-same-origin"
          loading="lazy"
        />
      </div>
    </section>
  );
}
